/**
 * Popup position corrector — desktop only.
 *
 * On some Chrome builds running on Windows at fractional display scaling
 * (seen on Chrome 152 at 125% / 150% on several Lenovo/other machines), AntD's
 * popup alignment (@rc-component/trigger's rc-align) computes a wildly
 * off-screen position for EVERY popup — the dropdown opens with its options but
 * lands thousands of pixels to the left (e.g. `inset: -3753px auto auto
 * -15211px`). To the user it reads as "the dropdown never shows".
 *
 * It is not our data, our layout, or the popup's container: with the field's
 * `getPopupContainer` in place (see main.jsx) the popup's offsetParent sits
 * exactly on the trigger — every input rc-align needs is correct — yet it still
 * adds a spurious ~15,000px. It reproduces only on those Windows/Chrome builds:
 * macOS, and Chromium with a forced fractional deviceScaleFactor, both position
 * correctly. So there is no bad input to fix and no way to reproduce it here to
 * patch rc-align itself.
 *
 * This is the defensive fix: watch for popups that have been placed off-screen
 * and snap them back under their own offsetParent — which, thanks to
 * getPopupContainer, is the trigger's own box. It only ever touches a popup that
 * is ALREADY off-screen, so on healthy machines (macOS, correct positioning) it
 * does nothing at all. Setting top/left doesn't change the popup's size, so it
 * doesn't retrigger rc-align's ResizeObserver — no fight, no loop.
 */

// Every AntD popup that aligns to a trigger and can therefore be flung off-screen.
const POPUP_SELECTOR = [
  '.ant-select-dropdown',
  '.ant-picker-dropdown',
  '.ant-cascader-dropdown',
  '.ant-tree-select-dropdown',
  '.ant-auto-complete-dropdown',
  '.ant-dropdown',
  '.ant-mentions-dropdown',
].join(',');

// A 4px slack keeps a popup that is merely flush with an edge from counting as
// "off-screen" — only a genuinely displaced one (the bug) trips this.
function isOffscreen(rect) {
  if (rect.width === 0 || rect.height === 0) return false; // not laid out / hidden
  return (
    rect.right < 4 ||
    rect.left > window.innerWidth - 4 ||
    rect.bottom < 4 ||
    rect.top > window.innerHeight - 4
  );
}

// The trigger a given popup belongs to. AntD marks the open control while its
// popup is up (`ant-select-open`, `ant-picker-focused`), and only one is open at
// a time, so that class is a reliable back-reference — more reliable than the
// offsetParent's height, which varies by wrapper and was placing the popup ON
// the field instead of below it.
function findTrigger(el) {
  const cl = el.classList;
  if (cl.contains('ant-select-dropdown') || cl.contains('ant-auto-complete-dropdown') || cl.contains('ant-cascader-dropdown') || cl.contains('ant-tree-select-dropdown')) {
    const open = document.querySelectorAll('.ant-select-open');
    return open[open.length - 1] || null;
  }
  if (cl.contains('ant-picker-dropdown')) {
    return document.querySelector('.ant-picker-focused') || null;
  }
  return null;
}

function correct(el) {
  // AntD keeps the node mounted and toggles a *-hidden class while closed.
  if (/-hidden(\s|$)/.test(el.className)) return;
  if (!isOffscreen(el.getBoundingClientRect())) return;

  const op = el.offsetParent;
  // The popup is position:absolute against its offsetParent. With the field's
  // getPopupContainer (main.jsx) that offsetParent is a box near the trigger, so
  // we can place the popup at an absolute top/left inside it. If it ever falls
  // back to document.body we can't, so leave it rather than move it wrong.
  if (!op || op === document.body || op === document.documentElement) return;
  const opRect = op.getBoundingClientRect();

  const trigger = findTrigger(el);
  if (trigger) {
    // Anchor to the field's real bottom-left, expressed relative to offsetParent.
    // This is immune to how tall the offsetParent happens to be.
    const t = trigger.getBoundingClientRect();
    el.style.setProperty('inset', 'auto', 'important');
    el.style.setProperty('top', `${Math.round(t.bottom - opRect.top + 2)}px`, 'important');
    el.style.setProperty('left', `${Math.round(t.left - opRect.left)}px`, 'important');
    el.style.setProperty('right', 'auto', 'important');
    el.style.setProperty('bottom', 'auto', 'important');
    return;
  }

  // Fallback for popups with no open-marked trigger: sit just below the box.
  el.style.setProperty('inset', 'auto', 'important');
  el.style.setProperty('top', `${op.offsetHeight}px`, 'important');
  el.style.setProperty('left', '0px', 'important');
  el.style.setProperty('right', 'auto', 'important');
  el.style.setProperty('bottom', 'auto', 'important');
}

/**
 * Install the corrector. Returns a cleanup function that disconnects it.
 * Call once, from the desktop shell.
 */
export default function installPopupPositionFix() {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return () => {};

  const scan = () => document.querySelectorAll(POPUP_SELECTOR).forEach(correct);

  // rc-align writes the popup's position into its inline `style`, and re-writes
  // it on open, scroll and resize — so watch style/class changes as well as new
  // nodes, and re-check on the next frame after any of them.
  let raf = 0;
  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      scan();
    });
  };

  const mo = new MutationObserver(schedule);
  mo.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

  // A popup can also drift off-screen on scroll/resize without a DOM mutation.
  window.addEventListener('scroll', schedule, true);
  window.addEventListener('resize', schedule);

  scan();

  return () => {
    mo.disconnect();
    window.removeEventListener('scroll', schedule, true);
    window.removeEventListener('resize', schedule);
    if (raf) cancelAnimationFrame(raf);
  };
}
